import type { KeycloakLoginOptions } from 'keycloak-js';
import { environment } from '../../environments/environment';

/**
 * Login options for the hostname the app is currently served from.
 *
 * On `parken.jku.at` every login carries `kc_idp_hint`, so Keycloak forwards straight to the JKU
 * identity provider and never renders its own login page. That is what JKU's design asks for —
 * JKU homepage → Quicklinks "Parken" → "Anmelden bei myJKU" → license-plate registration, with no
 * JJames screen in between (webshop concept V06, slides 8 and 18) — and it is the feedback JKU
 * gave on the shared Keycloak page: "it looks like a standard JJ-page with 1 button added
 * JKU-Login".
 *
 * `kc_idp_hint` is a per-authorization-request parameter, so this needs no second Keycloak client.
 * That matters: a second client would not inherit the `jjames-scope` client scope, and without it
 * tokens carry no `jjames-backend` audience — login would succeed and every API call would 401.
 *
 * Two overrides, both via a `login` query parameter:
 *   `?login=local` — never hint. For the one visitor the hint would otherwise trap: someone
 *                    reaching parken.jku.at without a myJKU account, who needs the password form.
 *   `?login=jku`   — always hint, whatever the hostname. Lets the JKU path be exercised on
 *                    localhost and on parker.jjames.eu, and gives JKU a link they can publish
 *                    that lands straight on myJKU.
 */
/**
 * Read once, at module load.
 *
 * The Angular router drops query parameters it does not know on its first redirect, so by the time
 * someone clicks Login the `?login=` parameter is usually gone from the address bar. Capturing it
 * during bootstrap — before any navigation — makes the override survive for the whole session.
 */
const loginOverride = new URLSearchParams(window.location.search).get('login');

export function loginOptions(extra: KeycloakLoginOptions = {}): KeycloakLoginOptions {
  const auth = environment.auth as { jkuHostname?: string; jkuIdpAlias?: string };
  const override = loginOverride;

  if (override === 'local') {
    return extra;
  }

  const onJkuHost = !!auth.jkuHostname && window.location.hostname === auth.jkuHostname;

  if (auth.jkuIdpAlias && (override === 'jku' || onJkuHost)) {
    return { idpHint: auth.jkuIdpAlias, ...extra };
  }

  return extra;
}

/**
 * True when the app is being served from the JKU hostname (or `?login=jku` forces that treatment).
 *
 * Everything JKU-specific keys off this rather than off the authenticated user's claims, because
 * the branding decision has to be made before anyone has signed in.
 */
export function isJkuHost(): boolean {
  if (loginOverride === 'local') {
    return false;
  }
  const auth = environment.auth as { jkuHostname?: string };
  return loginOverride === 'jku' || (!!auth.jkuHostname && window.location.hostname === auth.jkuHostname);
}

/**
 * Keycloak alias of the JKU identity provider for the current environment.
 *
 * Not a constant: the alias differs per realm (`jku` in production, `keycloak-oidc` in testing,
 * `oidc` in the local dev realm). A wrong value does not error — Keycloak ignores an unknown
 * `kc_idp_hint` and renders its own login page, which is precisely the screen JKU rejected.
 */
export function jkuIdpAlias(): string {
  return (environment.auth as { jkuIdpAlias?: string }).jkuIdpAlias ?? '';
}
